import { textEllipsis } from '@design-system/styles'
import { css } from '@emotion/react'
import { typo } from '@mathflat/common/style'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import { observer } from 'mobx-react'
import { useRef } from 'react'
import type { StyleProps } from 'src/types'

import Button from '@/component/atoms/Button'
import { Loader } from '@/component/atoms/Loader'
import { Switch } from '@/component/atoms/Switch/Switch'
import ModalTemplate from '@/component/templates/ModalTemplate'
import { assignCancelWarning } from '@/constant/modal/modalContent'
import { useGroupedStudentDualList } from '@/contentComponents/DualList/hooks/useGroupedStudentDualList'
import {
  STUDENT_LIST_OPEN_LABEL,
  useStudentListOpenState,
} from '@/contentComponents/DualList/hooks/useStudentListOpenState'
import StudentGroupedDualList from '@/contentComponents/DualList/StudentGroupedDualList'
import type { Student, StudentWorkbook, StudentWorksheet } from '@/dto'
import type { CommonError } from '@/dto/common/CommonError'
import commonRepo from '@/repository/common.repo'
import globalLoaderService from '@/service/globalLoader.service'
import modalService from '@/service/modal.service'
import { commonErrorMessage } from '@/utils/toast'

type StudentIds = Student['id'][]

/**
 * 초기 선택된 학생 정보
 * - studentDataRelationId가 있으면 "이미 출제됨"으로 표시되고 삭제 시 경고 모달 표시
 * - studentDataRelationId가 없으면 단순 선택 상태로 취급
 *
 * TODO: 2차원 배열을 1차원 배열로 변경 예정 (현재 2차원인 이유 없음, flat()해서 사용)
 */
export type AssignedStudent = {
  studentId: Student['id']
  /** 워크시트/워크북 ID (없으면 단순 선택 상태) */
  studentDataRelationId?: StudentWorksheet['id'] | StudentWorkbook['id']
  studentName?: string
}

/** 선택된 학생 타입 (다른 파일에서 import해서 사용) */
export type RightStudent = {
  studentId: Student['id']
  studentName: Student['name']
}

/** 슬롯과 버튼 설정이 참조하는 현재 선택 상태 */
export type SelectionContext = {
  /** 초기 오른쪽 목록에서 id 를 뺀다. 호출부가 서버에서 확정 처리한 학생을 "변경 없음" 으로 되돌릴 때 쓴다 */
  removeFromInitialState: (ids: Student['id'][]) => void
  /** 오른쪽에 있는 학생을 왼쪽으로 옮긴다. 서버에서 이미 빠진 학생을 화면에서도 빼 줄 때 쓴다 */
  moveToLeft: (ids: Student['id'][]) => void
  /** 초기 오른쪽 목록에 id 를 더한다. 서버에서 이미 확정된 학생을 "변경 없음" 으로 받아들일 때 쓴다 */
  addToInitialState: (ids: Student['id'][]) => void
  /** 왼쪽에 있는 학생을 오른쪽으로 옮긴다. 서버에서 이미 추가된 학생을 화면에도 보여 줄 때 쓴다 */
  moveToRight: (ids: Student['id'][]) => void
  /** 현재 선택된 전체 학생 */
  rightItems: RightStudent[]
  /** 이번에 새로 추가된 학생 (초기 상태에 없던 학생) */
  movedToRightItems: RightStudent[]
  /** 초기 상태 대비 변경 여부 */
  isChanged: boolean
}

type Props = {
  title: string
  titleDescription?: string
  /**
   * 초기 선택된 학생 목록
   * - studentDataRelationId가 있으면 "이미 출제됨" 표시 + 삭제 시 경고
   * - studentDataRelationId가 없으면 단순 선택 상태
   *
   * TODO: 1차원 배열로 변경 예정 (AssignedStudent[])
   */
  assignedLists?: AssignedStudent[][]
  /** 이미 출제된 학생 제거 시 경고 표시 여부 (default: true) */
  warnDeleteAssigned?: boolean
  /**
   * 새로 추가된 학생 제출 콜백 (기존에 없던 학생만 전달).
   * `false` 를 반환하면 모달을 닫지 않는다(호출부가 이미 안내를 띄운 실패). reject 되면 공통 에러 토스트 후 닫지 않는다
   */
  onSubmit?: (
    students: {
      id: Student['id']
      name: Student['name']
    }[],
  ) => void | false | Promise<void | false | CommonError>
  /** 이미 출제된 학생 제거 콜백 */
  onUnassign?: (
    ids: NonNullable<AssignedStudent['studentDataRelationId']>[],
    studentIds: StudentIds,
  ) => void | Promise<void> | boolean | Promise<boolean>
  /**
   * 선택 완료 콜백 (전체 선택된 학생 전달). Promise 를 반환하면 끝날 때까지 기다린 뒤 모달을 닫는다.
   * `false` 를 반환하면 닫지 않는다. reject 되면 공통 에러 토스트 후 닫지 않는다
   */
  onComplete?: (
    students: {
      studentId: Student['id']
      studentName: Student['name']
    }[],
  ) => void | false | Promise<void | false>
  /**
   * 확정 버튼을 눌렀을 때 로더 없이 먼저 실행. 사용자에게 묻는 얼럿은 여기서 띄운다(로더가 열린 뒤에는 얼럿이 가려진다).
   * false 면 확정을 중단하고 모달을 유지한다. reject 되면 공통 에러 토스트 후 중단한다.
   * 실행 중에는 확정 버튼을 다시 눌러도 무시된다(로더가 없어 입력이 막히지 않으므로)
   */
  beforeConfirm?: (ctx: SelectionContext) => boolean | Promise<boolean>
  /**
   * 확정 실행. 로더 아래에서 돌고 끝나면 모달을 닫는다. onSubmit·onComplete 대신 쓰는 단일 콜백이며 함께 주면 이것만 실행된다.
   * false 를 반환하면 호출부가 안내를 끝낸 실패로 보고 모달을 유지한다. reject 되면 공통 에러 토스트 후 유지
   */
  onConfirm?: (ctx: SelectionContext) => void | false | Promise<void | false>
  /** 오른쪽 아이템 추가 표시. undefined 를 반환하면 기본("이미 출제됨") 처리로 넘어간다 */
  renderRightItemExtra?: (student: Student) => React.ReactNode
  /** 왼쪽 이동 전에 먼저 가로챈다. false 면 이동을 취소하고, true 면 기본 출제 취소 경고로 이어진다 */
  onBeforeMoveToLeft?: (students: Student[], ctx: SelectionContext) => boolean | Promise<boolean>
  /** 확정 버튼 라벨. 기본 '선택 완료'. 함수면 선택 상태로 결정 */
  submitLabel?: string | ((ctx: SelectionContext) => string)
  /** 확정 버튼 비활성 조건. 기본 (ctx) => !ctx.isChanged. 주면 기본을 대체 */
  isSubmitDisabled?: (ctx: SelectionContext) => boolean
  /**
   * 푸터 위 전폭 슬롯. 로딩 중에는 호출되지 않으므로 콜백 본문에서 훅을 직접 쓰지 말고
   * 훅이 필요하면 별도 컴포넌트 엘리먼트를 반환한다
   */
  renderFooterExtra?: (ctx: SelectionContext) => React.ReactNode
  /** "이미 출제됨" 텍스트 커스터마이징 */
  renderAssignedText?: (studentId: Student['id']) => string
  /** 왼쪽 아이템에 추가 정보 표시 */
  renderLeftDescriptionElement?: (student: Student) => React.ReactElement | undefined
  /** 특정 학생 비활성화 조건 */
  confirmDisabledIds?: (students: Student[]) => Student['id'][]
  /** 경고 모달 내용 커스터마이징 */
  warningModalContent?: React.ReactElement
  /** 홈룸(담당 반) 권한을 무시하고 학원 전체 학생을 노출 */
  fetchAllStudents?: boolean
  /**
   * 화면에서만 숨긴다. 언마운트하지 않으므로 불러온 학생 목록과 선택 상태가 그대로 남는다.
   * 이 모달 위에 다른 뷰를 잠시 띄웠다가 돌아올 때 쓴다
   */
  hidden?: boolean
} & StyleProps

const AssignGroupStudentModal = ({
  title,
  titleDescription,
  assignedLists,
  warnDeleteAssigned = true,
  onSubmit,
  onUnassign,
  onComplete,
  renderAssignedText,
  renderLeftDescriptionElement,
  confirmDisabledIds,
  style,
  className = '',
  hidden = false,
  warningModalContent,
  fetchAllStudents,
  submitLabel = '선택 완료',
  isSubmitDisabled,
  renderFooterExtra,
  renderRightItemExtra,
  onBeforeMoveToLeft,
  beforeConfirm,
  onConfirm,
}: Props) => {
  // assignedLists를 1차원으로 flat
  const mergedAssignedLists: AssignedStudent[] = assignedLists?.flat() ?? []
  // studentDataRelationId가 있는 학생 Set (이미 출제됨, unassign 시 제거)
  const assignedWithRelationSetRef = useRef(
    new Set(
      mergedAssignedLists
        .filter(({ studentDataRelationId }) => studentDataRelationId !== undefined)
        .map(({ studentId }) => studentId),
    ),
  )
  const { isDefaultOpen, setIsDefaultOpen } = useStudentListOpenState({
    fallbackValue: commonRepo.academy?.가상학생등록여부,
  })

  // useStudentDualList (학생 데이터 fetch + DualList 상태 관리)
  const { dualListBox, rightItems, isLoading, isChanged, movedToRightItems, groupedDualListProps } =
    useGroupedStudentDualList({
      initialRightItemIds: mergedAssignedLists.map(({ studentId }) => studentId),
      defaultOpen: isDefaultOpen,
      ignoreHomeroom: fetchAllStudents,
    })

  const toRightStudents = (items: Student[]): RightStudent[] =>
    items.map((item) => ({ studentId: item.id, studentName: item.name }))
  const selection: SelectionContext = {
    rightItems: toRightStudents(rightItems),
    movedToRightItems: toRightStudents(movedToRightItems),
    isChanged,
    removeFromInitialState: (ids) => dualListBox?.removeFromInitialState(ids),
    moveToLeft: (ids) => dualListBox?.moveRightToLeft(ids),
    addToInitialState: (ids) => dualListBox?.addToInitialState(ids),
    moveToRight: (ids) => dualListBox?.moveLeftToRight(ids),
  }
  const resolvedSubmitLabel =
    typeof submitLabel === 'function' ? submitLabel(selection) : submitLabel
  const resolvedSubmitDisabled = isSubmitDisabled ? isSubmitDisabled(selection) : !isChanged

  // 왼쪽/오른쪽 아이템 추가 정보 렌더링
  const renderItemExtra = ({ item, side }: { item: Student; side: 'left' | 'right' }) => {
    if (side === 'left') {
      return renderLeftDescriptionElement?.(item)
    }
    const extra = renderRightItemExtra?.(item)
    if (extra !== undefined) return extra
    // studentDataRelationId가 있는 경우에만 "이미 출제됨" 표시
    if (side === 'right' && assignedWithRelationSetRef.current.has(item.id)) {
      return (
        <span css={S.assignedLabel}>
          {renderAssignedText?.(item.id) ?? `이미 ${tJosa('출제')}됨`}
        </span>
      )
    }
    return null
  }
  // 비활성화 필터
  const disabledFilter = (student: Student) => {
    if (!confirmDisabledIds) return false
    const disabledIds = confirmDisabledIds([student])
    return disabledIds.includes(student.id)
  }
  // 왼쪽으로 이동 전 콜백 (이미 출제된 학생 경고)
  const handleBeforeMoveToLeft = async (items: Student[]): Promise<boolean> => {
    if (onBeforeMoveToLeft && !(await onBeforeMoveToLeft(items, selection))) {
      return false
    }
    // studentDataRelationId가 있는 학생만 경고 대상
    const assignedItems = items.filter((item) => assignedWithRelationSetRef.current.has(item.id))
    // 경고 대상이 없으면 바로 이동
    if (assignedItems.length === 0) {
      return true
    }
    // 경고 비활성화된 경우
    if (!warnDeleteAssigned) {
      return true
    }
    // 경고 모달 표시
    return new Promise((resolve) => {
      modalService.openWarningModal({
        content: warningModalContent ?? assignCancelWarning.default(),
        onClick: async () => {
          try {
            // 삭제할 studentDataRelationId 수집
            const idsToUnassign: NonNullable<AssignedStudent['studentDataRelationId']>[] = []
            const studentIdsToUnassign: Student['id'][] = []
            for (const item of assignedItems) {
              const relations = mergedAssignedLists
                .filter((a) => a.studentId === item.id && a.studentDataRelationId !== undefined)
                .map((a) => a.studentDataRelationId!)
              if (relations.length > 0) {
                idsToUnassign.push(...relations)
                studentIdsToUnassign.push(item.id)
              }
            }
            const isNotCompleted = await onUnassign?.(idsToUnassign, studentIdsToUnassign)
            if (!isNotCompleted) {
              // unassign 성공: Set에서 제거 + 초기 상태에서 해당 ID 제거
              for (const id of studentIdsToUnassign) {
                assignedWithRelationSetRef.current.delete(id)
              }
              dualListBox?.removeFromInitialState(studentIdsToUnassign)
            }
            resolve(!isNotCompleted)
          } catch (error) {
            resolve(false)
          }
        },
      })
    })
  }
  // 제출 처리
  // beforeConfirm 은 로더 없이 돌아 입력이 열려 있으므로, 응답을 기다리는 동안의 재진입을 막는다
  const isConfirmingRef = useRef(false)
  const handleSubmit = async () => {
    if (isConfirmingRef.current) return
    isConfirmingRef.current = true
    try {
      if (beforeConfirm && !(await beforeConfirm(selection))) return
    } catch (error) {
      commonErrorMessage(error as CommonError)
      return
    } finally {
      isConfirmingRef.current = false
    }
    globalLoaderService.openLoader()
    try {
      if (onConfirm) {
        if ((await onConfirm(selection)) === false) return
        modalService.closeModal()
        return
      }
      // 전체 선택된 학생 콜백
      if ((await onComplete?.(selection.rightItems)) === false) return
      // 새로 추가된 학생 제출 (movedToRightItems = 초기 left → right로 이동한 학생)
      if (movedToRightItems.length > 0 && onSubmit) {
        const result = await onSubmit(
          movedToRightItems.map((item) => ({
            id: item.id,
            name: item.name,
          })),
        )
        if (result === false) return
      }
      modalService.closeModal()
    } catch (error) {
      commonErrorMessage(error as CommonError)
    } finally {
      globalLoaderService.closeLoader()
    }
  }
  // 로딩 중
  if (isLoading) {
    return (
      <ModalTemplate
        css={[S.modal, hidden && S.hidden]}
        style={style}
        className={className}
        header={
          <header css={S.header}>
            <h3 className="title">{title}</h3>
            {titleDescription && <sub data-testid="assign-modal-target">{titleDescription}</sub>}
          </header>
        }
        body={
          <div css={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        }
        footer={<footer css={S.footer} />}
      />
    )
  }

  return (
    <ModalTemplate
      css={[S.modal, hidden && S.hidden]}
      style={style}
      className={className}
      header={
        <header css={S.header}>
          <h3 className="title">{title}</h3>
          {titleDescription && <sub data-testid="assign-modal-target">{titleDescription}</sub>}
        </header>
      }
      body={
        <div css={S.body}>
          <StudentGroupedDualList
            dualListBox={dualListBox}
            searchPlaceholder="학생 이름 검색"
            rightHeaderTitle="선택된 학생"
            renderItemExtra={renderItemExtra}
            disabledFilter={confirmDisabledIds ? disabledFilter : undefined}
            onBeforeMoveToLeft={handleBeforeMoveToLeft}
            {...groupedDualListProps}
          />
        </div>
      }
      footer={
        <footer css={S.footer}>
          {renderFooterExtra?.(selection)}
          <div className="footer-row">
            <div className="toggle-area">
              <Switch
                size="s"
                checked={isDefaultOpen}
                onCheckedChange={() => setIsDefaultOpen(!isDefaultOpen)}
              />
              <span>{STUDENT_LIST_OPEN_LABEL}</span>
            </div>
            <div className="button-area">
              <Button
                width={120}
                size="l"
                variant="solid"
                color="accent"
                disabled={resolvedSubmitDisabled}
                onClick={handleSubmit}
              >
                {resolvedSubmitLabel}
              </Button>
            </div>
          </div>
        </footer>
      }
    />
  )
}

const S = {
  hidden: css`
    display: none;
  `,
  modal: css`
    width: 600px;
    height: 620px;
  `,
  header: css`
    display: flex;
    align-items: center;
    height: 72px;
    padding: 24px;
    border-bottom: 1px solid var(--Colors-Gray-400);
    > .title {
      flex-shrink: 0;
      font-weight: 700;
      ${typo.heading05};
    }
    > sub {
      margin: 0px 24px 0px 12px;
      color: var(--Colors-Gray-700);
      ${typo.body02};
      ${textEllipsis(2)};
    }
  `,
  body: css`
    display: flex;
    flex: 1;
    padding: 16px 24px 0 24px;
    overflow: hidden;
  `,
  footer: css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px 24px 20px 24px;
    .footer-row {
      display: flex;
      justify-content: space-between;
    }
    .toggle-area {
      display: flex;
      gap: 8px;
      margin-top: auto;
      margin-bottom: auto;
    }
  `,
  assignedLabel: css`
    display: flex;
    align-items: center;
    color: var(--Colors-Gray-700);
    font-weight: 500;
    ${typo.caption02};

    &::before {
      content: '';
      display: inline-block;
      width: 6px;
      height: 6px;
      border-radius: var(--Radius-Circle);
      background-color: var(--Colors-Sky-400);
      margin-right: 4px;
    }
  `,
}

export default observer(AssignGroupStudentModal)
